/**
 * This file is part of the Goobi viewer - a content presentation and management
 * application for digitized objects.
 * 
 * Visit these websites for more information. - http://www.intranda.com -
 * http://digiverso.com
 * 
 * This program is free software; you can redistribute it and/or modify it under the terms
 * of the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A
 * PARTICULAR PURPOSE. See the GNU General Public License for more details.
 * 
 * You should have received a copy of the GNU General Public License along with this
 * program. If not, see <http://www.gnu.org/licenses/>.
 * 
 * Module which draws rectangles to the image to highlight areas.
 * 
 * @version 3.2.0
 * @module viewImage.drawRect
 * @requires jQuery
 */

import HitAreas from "./HitAreas.js";
import { fromEvent } from "rxjs";
import { filter } from "rxjs/operators";

const settings  = {
    style : {
        className: "imageview-overlay"
    },
    rotateWithImage: true,
    element: undefined
}

const _debug = false;

let idCounter = 1;

class Overlay {

    constructor(bounds, config, id) {
        this.bounds = new OpenSeadragon.Rect(bounds.x, bounds.y, bounds.width, bounds.height);
        this.config = jQuery.extend(true, {}, settings, config)
        this.id = id ?? _getId();
        
        if(this.element) {
            this.remove();
        }
        if(this.config.element) {
            this.element = this.config.element;
        }
    }

    createElement() {
        this.element = document.createElement("div");
        this.element.id = this.id;
        this.element.className = this.config.style.className;
        if(this.config.style.borderColor && this.config.style.borderWidth) {
            this.element.style.border = `${this.config.style.borderWidth}px solid ${this.config.style.borderColor}`;
        }
        if(this.config.style.fillColor) {
            this.element.style.backgroundColor = this.config.style.fillColor;
            if(this.config.style.fillOpacity) {
                this.element.style.opacity = this.config.style.fillOpacity;
            }
        }
    }

    draw(viewer) {
        if(!this.element) {
            this.createElement();
        }
        this.viewer = viewer;
        if(_debug)console.log("add overlay at ", this.bounds, this.element);
        return new Promise( (resolve, reject) => {
            this.viewer.openseadragon.addOnceHandler('add-overlay', e => {
                if(_debug)console.log("drew overlay ", this.id, this.osOverlay);
                resolve(this);
            });
            this.viewer.openseadragon.addOverlay(
                {
                    element:this.element, 
                    location: this.bounds.getCenter(),
                    width: this.bounds.width,
                    height: this.bounds.height,
                    placement: OpenSeadragon.Placement.CENTER,
                    rotationMode: this.config.rotateWithImage ? OpenSeadragon.OverlayRotationMode.EXACT : OpenSeadragon.OverlayRotationMode.NO_ROTATION
                }
            );
            this.osOverlay = this.viewer.openseadragon.getOverlayById(this.id);
        });
    }

    remove() {
        if(this.element) {
            this.viewer?.openseadragon?.removeOverlay(this.element);
            this.osOverlay = undefined;
            this.element = undefined;
        }
    }

    onClick() {
        return this.viewer.onClick.pipe(
            filter(e => this.contains(_getLocation(e.originalEvent ?? e, this.viewer)))
        );
    }

    moveBy(distance, canvasCoords) {

        if(canvasCoords) {
            const boundsInCanvas = this.viewer?.openseadragon?.viewport?.viewportToViewerElementRectangle(this.bounds);
            const updatedTopLeft = boundsInCanvas.getTopLeft().plus(distance);
            this.moveTo(updatedTopLeft, true);
        } else {
            const updatedTopLeft = this.bounds.getTopLeft().plus(distance);
            this.moveTo(updatedTopLeft, false);
        }
    }

    convertFromCanvasToViewport(coords, viewport) {
        viewport ??= this.viewer?.openseadragon?.viewport;
        if(viewport === undefined) {
            throw "Must provide viewport for coordinate conversion or invoke draw before";
        }
        if(coords instanceof OpenSeadragon.Rect) {
            if(this.config.rotateWithImage) {
                return viewport.viewerElementToViewportRectangle(coords);
            } else {
                return ImageView.CoordinateConversion.convertCoordinatesFromCanvasToImage(coords, this.viewer.openseadragon);
            }
        } else if (coords instanceof OpenSeadragon.Point) {
            if(this.config.rotateWithImage) {
                return viewport.viewerElementToViewportCoordinates(coords);
            } else {
                return ImageView.CoordinateConversion.convertPointFromCanvasToImage(coords, this.viewer.openseadragon);
            }
        } else {
            throw "Unkown object to convert " + coords;
        }
    }

    convertFromViewportToCanvas(coords, viewport) {
        viewport ??= this.viewer?.openseadragon?.viewport;
        if(viewport === undefined) {
            throw "Must provide viewport for coordinate conversion or invoke draw before";
        }
        if(coords instanceof OpenSeadragon.Rect) {
            if(this.config.rotateWithImage) {
                return viewport.viewportToViewerElementRectangle(coords);
            } else {
                return ImageView.CoordinateConversion.convertCoordinatesFromImageToCanvas(coords, this.viewer.openseadragon);
            }
        } else if (coords instanceof OpenSeadragon.Point) {
            if(this.config.rotateWithImage) {
                return viewport.viewportToViewerElementCoordinates(coords);
            } else {
                return ImageView.CoordinateConversion.convertPointFromImageToCanvas(coords, this.viewer.openseadragon);
            }
        } else {
            throw "Unkown object to convert " + coords;
        }
    }

    convertFromViewportToImage(coords, viewport) {
        viewport ??= this.viewer?.openseadragon?.viewport;
        if(viewport === undefined) {
            throw "Must provide viewport for coordinate conversion or invoke draw before";
        }
        if(coords instanceof OpenSeadragon.Rect) {
            if(this.viewer?.openseadragon) {
                let imageRectUnrotated = ImageView.CoordinateConversion.convertRectFromOpenSeadragonToImage(coords, this.viewer.openseadragon, this.viewer.getOriginalImageSize());
                let imageRectRotated = ImageView.CoordinateConversion.convertImageAreaFromRotatedImageToImage(imageRectUnrotated, this.viewer.getRotation(), this.viewer.getOriginalImageSize());
                if(_debug)("Convert coords from ", coords, viewport.viewportToImageRectangle(coords), imageRectRotated);
            }
            return viewport.viewportToImageRectangle(coords);
        } else if (coords instanceof OpenSeadragon.Point) {
            return viewport.viewportToImageCoordinates(coords);
        } else {
            throw "Unkown object to convert " + coords;
        }
    }

    convertFromImageToViewport(coords, viewport) {
        viewport ??= this.viewer?.openseadragon?.viewport;
        if(viewport === undefined) {
            throw "Must provide viewport for coordinate conversion or invoke draw before";
        }
        if(coords instanceof OpenSeadragon.Rect) {
            return viewport.imageToViewportRectangle(coords);
        } else if (coords instanceof OpenSeadragon.Point) {
            return viewport.imageToViewportCoordinates(coords);
        } else {
            throw "Unkown object to convert " + coords;
        }
    }

    moveTo(coords) {
        this.osOverlay.update(coords);
        if(this.viewer?.openseadragon?.viewport) {
            this.bounds = this.osOverlay.getBounds(this.viewer.openseadragon.viewport);
            this.viewer.openseadragon.forceRedraw();
        } else {
            this.bounds = coords;
        }
    }

    setBounds(rect) {
        if(this.osOverlay) {
            this.osOverlay.update({
                location: rect.getCenter(),
                width: rect.width,
                height: rect.height,
                placement: OpenSeadragon.Placement.CENTER
            });
            if(this.config.rotateWithImage) {
                this.bounds = this.osOverlay.getBounds(this.viewer.openseadragon.viewport);
            } else {
                this.bounds = rect;
            }
        }
        this.viewer?.openseadragon?.forceRedraw();
    }

    getBoundsInCanvas() {
        if(!this.osOverlay) {
            this.osOverlay = this.viewer.openseadragon.getOverlayById(this.id);
        }
        if(this.osOverlay) {
            if(this.config.rotateWithImage) {
                return this.viewer.openseadragon.viewport.viewportToViewerElementRectangle(this.osOverlay.getBounds(this.viewer.openseadragon.viewport));
            }
            const element = this.osOverlay.elementWrapper || this.osOverlay.element;
            const x = element.offsetLeft;
            const y = element.offsetTop;
            const width = element.offsetWidth;
            const height = element.offsetHeight;
            return new OpenSeadragon.Rect(x,y, width, height);
        } else {
            throw "Overlay must be drawn on canvas before getting canvas bounds";
        }
    }

    getBoundsInImage() {
        return this.convertFromViewportToImage(this.getBounds(), this.viewer.openseadragon.viewport);
    }

    getBounds() {
        return this.bounds;
    }
    

    contains(point, delta) {
        return this.bounds.containsPoint(point, delta ?? 0);
    }

    getHitArea(point, delta) {
        const rect = this.getBounds();
        if(_isInside(rect, point, delta)) {
            var area = _findCorner(rect, point, delta);
            if(!area) {
                area = _findEdge(rect, point, delta);
            }
            if(this.allowMove && !area && _isInside(rect, point, 0)){
                area = HitAreas.CENTER;
            }
          //prohibit fixed areas
            if(_isFixed(area, this.fixed)) {
                return HitAreas.FIXED;
            }
        }
        return area;
    }

    static convertStringToRect(string) {
        var parts = string.split(",");
        if(parts && parts.length == 4) {            
            var rect = new OpenSeadragon.Rect(parseFloat(parts[0]), parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
            return rect;
        } else {
            throw "Cannot convert string '" + string + "' to Rectangle";
        }
    }

    static convertRectToString(rect, decimalPlaces) {
        if(!decimalPlaces) {
            decimalPlaces = 0;
        }
        return rect.x.toFixed(decimalPlaces) + "," +  rect.y.toFixed(decimalPlaces) + "," +  rect.width.toFixed(decimalPlaces) + "," +  rect.height.toFixed(decimalPlaces);
    }
}

function _normalizeRotation(rotation) {
    return ((rotation % 360) + 360) % 360;
}

function _getLocation(event, viewer) {
    const point = new OpenSeadragon.Point(event.clientX + window.scrollX, event.clientY + window.scrollY);
    const coords = viewer.openseadragon.viewport.windowToViewportCoordinates(point);
    return coords;
}

function _getId() {
    return "imageview_overlay_" + idCounter++;
}

    function _isInside( rect, point, extra ) {
        return point.x > (rect.getTopLeft().x - extra) && point.x < ( rect.getBottomRight().x + extra ) && 
        point.y > (rect.getTopLeft().y - extra)  && point.y < ( rect.getBottomRight().y + extra );
    }

    /*
     * Determine the side of the rectangle rect the point lies on or closest at <=maxDist
     * distance
     */
    function _findEdge( rect, point, maxDist ) {
        var distanceToLeft = _distToSegment( point, rect.getTopLeft(), rect.getBottomLeft() );
        var distanceToBottom = _distToSegment( point, rect.getBottomLeft(), rect.getBottomRight() );
        var distanceToRight = _distToSegment( point, rect.getTopRight(), rect.getBottomRight() );
        var distanceToTop = _distToSegment( point, rect.getTopLeft(), rect.getTopRight() );
        
        var minDistance = Math.min( distanceToLeft, Math.min( distanceToRight, Math.min( distanceToTop, distanceToBottom ) ) );
        if ( minDistance <= maxDist ) {
            if ( distanceToLeft === minDistance ) {
                return HitAreas.LEFT;
            }
            if ( distanceToRight === minDistance ) {
                return HitAreas.RIGHT;
            }
            if ( distanceToTop === minDistance ) {
                return HitAreas.TOP;
            }
            if ( distanceToBottom === minDistance ) {
                return HitAreas.BOTTOM;
            }
        }
        return "";
    }
    
    function _isFixed(area, fixed) {
        if(area && fixed) {
            switch(area) {
                case HitAreas.TOP: 
                    return fixed.y || fixed.height;
                case HitAreas.BOTTOM: 
                    return fixed.height;
                case HitAreas.LEFT: 
                    return fixed.x || fixed.width;
                case HitAreas.RIGHT: 
                    return fixed.width;
                case HitAreas.TOPLEFT: 
                    return fixed.x || fixed.y || fixed.width || fixed.height;
                case HitAreas.TOPRIGHT: 
                    return fixed.y || fixed.width || fixed.height;
                case HitAreas.BOTTOMLEFT: 
                    return fixed.height || fixed.x || fixed.width;
                case HitAreas.BOTTOMRIGHT: 
                    return fixed.width || fixed.height;
                case HitAreas.CENTER: 
                    return fixed.y || fixed.x;
                case HitAreas.FIXED:
                    return true;
            }
        }
        return false;
    }

    /*
     * Determine the cornder of the rectangle rect the point lies on or closest at
     * <=maxDist distance
     */
    function _findCorner( rect, point, maxDist ) {
        var distanceToTopLeft = _dist( point, rect.getTopLeft() );
        var distanceToBottomLeft = _dist( point, rect.getBottomLeft() );
        var distanceToTopRight = _dist( point, rect.getTopRight() );
        var distanceToBottomRight = _dist( point, rect.getBottomRight() );
        
        var minDistance = Math.min( distanceToTopLeft, Math.min( distanceToTopRight, Math.min( distanceToBottomLeft, distanceToBottomRight ) ) );
        if ( minDistance <= maxDist ) {
            if ( distanceToTopLeft === minDistance ) {
                return HitAreas.TOPLEFT;
            }
            if ( distanceToTopRight === minDistance ) {
                return HitAreas.TOPRIGHT;
            }
            if ( distanceToBottomLeft === minDistance ) {
                return HitAreas.BOTTOMLEFT;
            }
            if ( distanceToBottomRight === minDistance ) {
                return HitAreas.BOTTOMRIGHT;
            }
        }
        return "";
    }

    function _sqr( x ) {
        return x * x
    }
    function _dist2( v, w ) {
        return _sqr( v.x - w.x ) + _sqr( v.y - w.y )
    }
    function _dist( v, w ) {
        return Math.sqrt( _dist2( v, w ) )
    }
    function _distToSegmentSquared( p, v, w ) {
        var l2 = _dist2( v, w );
        if ( l2 == 0 )
            return _dist2( p, v );
        var t = ( ( p.x - v.x ) * ( w.x - v.x ) + ( p.y - v.y ) * ( w.y - v.y ) ) / l2;
        if ( t < 0 )
            return _dist2( p, v );
        if ( t > 1 )
            return _dist2( p, w );
        return _dist2( p, {
            x: v.x + t * ( w.x - v.x ),
            y: v.y + t * ( w.y - v.y )
        } );
    }
    function _distToSegment( point, lineP1, lineP2 ) {
        return Math.sqrt( _distToSegmentSquared( point, lineP1, lineP2 ) );
    }

export default Overlay;